'use client';

import type { CalendarDate, CalendarDateTime, ZonedDateTime } from '@internationalized/date';
import clsx from 'clsx';
import { type ForwardedRef, useContext, useImperativeHandle, useRef } from 'react';
import {
  type CalendarProps as RACCalendarProps,
  DateInput as RACDateInput,
  DatePicker as RACDatePicker,
  type DatePickerProps as RACDatePickerProps,
  DatePickerStateContext as RACDatePickerStateContext,
  DateSegment as RACDateSegment,
  Dialog as RACDialog,
  Group as RACGroup,
  Popover as RACPopover,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCalendarEmpty } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { IconButton } from '../button/icon-button.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { ApiProps, CommonProps, FieldProps, FocusableElement } from '../types.js';
import { textStyle } from '../typography/utils.js';
import { DatePickerCalendar } from './date-picker-calendar.js';

/** Represents a date with optional time and timezone components. */
export type DateValue = CalendarDate | CalendarDateTime | ZonedDateTime;

/** Maps a date value to a specific subtype depending on which components it contains. */
export type MappedDateValue<T> = T extends ZonedDateTime
  ? ZonedDateTime
  : T extends CalendarDateTime
    ? CalendarDateTime
    : T extends CalendarDate
      ? CalendarDate
      : never;

export interface DatePickerProps<T extends DateValue>
  extends
    CommonProps,
    FieldProps<MappedDateValue<T>>,
    ApiProps<DatePickerApi>,
    Pick<
      RACDatePickerProps<T>,
      | 'value'
      | 'defaultValue'
      | 'placeholderValue'
      | 'onChange'
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'autoFocus'
      | 'onFocus'
      | 'onBlur'
      | 'isOpen'
      | 'defaultOpen'
      | 'onOpenChange'
      | 'minValue'
      | 'maxValue'
      | 'isDateUnavailable'
      | 'granularity'
      | 'firstDayOfWeek'
      | 'autoComplete'
    >,
    Pick<RACCalendarProps<T>, 'focusedValue' | 'defaultFocusedValue'> {}

/** The imperative API exposed by the `DatePicker` component. */
export interface DatePickerApi {
  /** Focuses the first editable segment in the input field. */
  focus: () => void;
}

function DatePicker<T extends DateValue>(
  {
    label,
    description,
    error: errorMessage,
    focusedValue,
    defaultFocusedValue,
    UNSAFE_className,
    UNSAFE_style,
    apiRef,
    ...props
  }: DatePickerProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLDivElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('DatePicker requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  const messages = useLocalizedMessages('datePicker');

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () =>
        (inputRef.current?.querySelector('[role=spinbutton]:not([data-disabled])') as FocusableElement | null)?.focus(),
    }),
    [],
  );

  return (
    <RACDatePicker {...props} ref={ref} className={clsx('cim-date-picker', UNSAFE_className)} style={UNSAFE_style}>
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <RACGroup className="cim-date-picker-input-group" data-readonly={props.isReadOnly || undefined}>
        <RACDateInput ref={inputRef} className={clsx('cim-date-picker-input', textStyle({ variant: 'body' }))}>
          {(segment) => (
            <span className="cim-date-picker-segment-wrapper">
              <RACDateSegment segment={segment} />
            </span>
          )}
        </RACDateInput>
        <div className="cim-date-picker-input-controls">
          {!props.isDisabled && !props.isReadOnly && <DatePickerClearButton />}
          <IconButton
            icon={<IconCalendarEmpty />}
            aria-label={messages.format('openCalendar')}
            variant="tertiary"
            size="small"
          />
        </div>
      </RACGroup>
      <FormFieldError>{errorMessage}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
      <RACPopover
        className="cim-date-picker-popover"
        placement="bottom end"
        offset={5} // 1px border + 4px actual offset
        containerPadding={16}
        data-cim-style-root
      >
        <RACDialog>
          <DatePickerCalendar<T>
            focusedValue={focusedValue}
            defaultFocusedValue={defaultFocusedValue}
            placeholderValue={props.placeholderValue}
          />
        </RACDialog>
      </RACPopover>
    </RACDatePicker>
  );
}

/**
 * Allows users to enter or select a date and time value.
 *
 * See [date picker usage guidelines](https://ui.cimpress.io/components/date-picker/).
 */
const _DatePicker = withStyleProps(forwardRef(DatePicker), 'DatePicker');

export { _DatePicker as DatePicker };

function DatePickerClearButton() {
  const datePickerState = useContext(RACDatePickerStateContext)!;
  const messages = useLocalizedMessages('datePicker');

  if (!datePickerState.value) {
    return null;
  }

  return (
    <IconButton
      // Don't inherit default Button behavior from DatePicker
      slot={null}
      UNSAFE_className="cim-date-picker-clear"
      aria-label={messages.format('clearValue')}
      onPress={() => datePickerState.setValue(null)}
      icon={<IconCloseDetached />}
      variant="tertiary"
      size="small"
    />
  );
}

// TODO: use one of our standard icons instead of this custom one
function IconCloseDetached() {
  return (
    <svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" className="cim-icon" fill="currentcolor" data-icon>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L6 4.93934L10.7197 0.21967C11.0126 -0.0732233 11.4874 -0.0732233 11.7803 0.21967C12.0732 0.512563 12.0732 0.987437 11.7803 1.28033L7.06066 6L11.7803 10.7197C12.0732 11.0126 12.0732 11.4874 11.7803 11.7803C11.4874 12.0732 11.0126 12.0732 10.7197 11.7803L6 7.06066L1.28033 11.7803C0.987437 12.0732 0.512563 12.0732 0.21967 11.7803C-0.0732233 11.4874 -0.0732233 11.0126 0.21967 10.7197L4.93934 6L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z"
      />
    </svg>
  );
}
